import React from 'react';
import { formatShortTimeDuration } from '../../utils/timeFormat';
import Tooltip from '../Tooltip';
import './MetricsCards.css';

const MetricsCards = ({ data, timeRange, selectedMetric, onMetricSelect, deviceBreakdown, filters, topGeos, onGeoFilter, selectedSite, user }) => {
  const isDemoUser = user?.email === 'demo@example.com';
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(2)}K`;
    }
    return `$${amount.toFixed(2)}`;
  };

  const formatChange = (change) => {
    if (change === null || change === undefined) {
      return null; // Return null to indicate no comparison available
    }
    const sign = change > 0 ? '+' : '';
    return `${sign}${change.toFixed(1)}%`;
  };

  const formatTimeRangeDisplay = () => {
    // Handle custom date range object
    if (typeof timeRange === 'object' && timeRange?.custom) {
      const startDate = new Date(timeRange.startDate);
      const endDate = new Date(timeRange.endDate);
      const diffTime = Math.abs(endDate - startDate);
      const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return days;
    }
    // Handle numeric timeRange
    if (typeof timeRange === 'number') {
      return timeRange;
    }
    // Fallback
    return 'N';
  };

  // Check if viewing today or yesterday (incomplete engagement data)
  const isViewingRecentData = () => {
    if (typeof timeRange === 'object' && timeRange?.custom) {
      const today = new Date().toISOString().split('T')[0];
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];
      const endDateStr = timeRange.endDate;
      return endDateStr === today || endDateStr === yesterdayStr;
    }
    // Check if timeRange is today (0) or yesterday (1)
    return timeRange === 0 || timeRange === 1;
  };

  const getChangeIcon = (change) => {
    if (change > 0) {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 3l5 5H3z" />
        </svg>
      );
    }
    if (change < 0) {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 13l-5-5h10z" />
        </svg>
      );
    }
    return null;
  };

  // Get sub-stats from API data
  const newUsers = data.newUsers?.value || 0;
  const returnUsers = data.returnUsers?.value || 0;
  const usersPerSession = data.usersPerSession?.value ? data.usersPerSession.value.toFixed(2) : '0';
  const pageviewsPerUser = data.pageviewsPerUser?.value ? data.pageviewsPerUser.value.toFixed(2) : '0';
  const returningUsersPercentage = data.returningUsersPercentage?.value || 0;
  const engagedSessionsPerUser = data.engagedSessionsPerUser?.value ? data.engagedSessionsPerUser.value.toFixed(2) : '0';
  const engagementTime = data.engagementTime?.value || 0; // Site-wide engagement time
  const returningUserEngagementTime = data.returningUserEngagementTime?.value || data.engagementTime?.value || 0; // Returning user engagement time

  const metrics = [
    {
      key: 'users',
      label: 'Overview',
      value: data.users.value,
      valueLabel: 'Total Users',
      change: data.users.change,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
        </svg>
      ),
      positive: true,
      clickable: true,
      tooltip: "Total unique visitors to your website over the selected time period. Each person is counted only once, regardless of how many times they visit.",
      subStats: [
        { key: 'newUsers', label: 'New', value: newUsers, change: data.newUsers?.change || 0, clickable: true, tooltip: 'Users who visited your site for the first time during the selected period.', metricId: 'newUsers' },
        { key: 'returnUsers', label: 'Return', value: returnUsers, change: data.returnUsers?.change || 0, clickable: true, tooltip: 'Users who have visited your site before and returned during the selected period.', metricId: 'returnUsers' }
      ],
      deviceBreakdown: deviceBreakdown,
      showDeviceBreakdown: true
    },
    {
      key: 'returnUsers',
      label: 'Loyalty',
      value: returnUsers,
      valueLabel: 'Returning Users',
      change: data.returnUsers?.change || null,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      positive: true,
      clickable: true,
      tooltip: "GA4-native loyalty metrics showing your returning visitors and engagement. Returning users are visitors who have been to your site before.",
      subStats: [
        { key: 'returningUsersPercentage', label: '% Returning', value: `${returningUsersPercentage.toFixed(1)}%`, change: data.returningUsersPercentage?.change || null, clickable: true, tooltip: 'Percentage of total users who are returning visitors.', metricId: 'returningUsersPercentage' },
        { key: 'engagedSessionsPerUser', label: 'Engaged Sessions/User', value: engagedSessionsPerUser, change: data.engagedSessionsPerUser?.change || null, clickable: true, tooltip: 'Average number of engaged sessions per returning user. Shows how engaged your returning visitors are.', metricId: 'engagedSessionsPerUser' },
        { key: 'returningUserEngagementTime', label: 'Avg Engagement Time', value: formatShortTimeDuration(Math.round(returningUserEngagementTime)), change: data.returningUserEngagementTime?.change || null, clickable: true, tooltip: 'Average time returning users spent actively engaged with your site.', metricId: 'engagementTime' }
      ]
    },
    {
      key: 'sessions',
      label: 'Scale',
      value: data.sessions.value,
      valueLabel: 'Sessions',
      change: data.sessions.change,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 3v18h18" />
          <path d="M7 12l4-4 4 4 6-6" />
        </svg>
      ),
      positive: true,
      clickable: true,
      tooltip: "Total traffic volume metrics. Sessions are individual visits to your website. A session ends after 30 minutes of inactivity or at midnight.",
      subStats: [
        { key: 'pageviews', label: 'Pageviews', value: data.pageviews.value.toLocaleString(), change: data.pageviews.change || 0, clickable: true, tooltip: 'Total number of pages viewed. Repeated views of a single page are counted.', metricId: 'pageviews' },
        { key: 'adImpressions', label: 'Ad Impressions', value: data.adImpressions?.value === null || data.adImpressions?.value === undefined ? null : data.adImpressions.value.toLocaleString(), change: data.adImpressions?.change || null, clickable: true, tooltip: 'Total number of times ads were displayed on your pages.', metricId: 'adImpressions' }
      ]
    },
    {
      key: 'bounceRate',
      label: 'Stickiness',
      value: `${data.bounceRate.value.toFixed(1)}%`,
      valueLabel: 'Bounce Rate',
      change: data.bounceRate.change,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ),
      positive: false,
      clickable: true,
      tooltip: "User engagement and retention metrics. Lower bounce rates and higher users per session indicate better stickiness.",
      subStats: [
        { key: 'usersPerSession', label: 'Users/Session', value: usersPerSession, change: data.usersPerSession?.change || 0, clickable: true, tooltip: 'Average number of users per session. Values greater than 1 may indicate shared devices.', metricId: 'usersPerSession' },
        { key: 'pageviewsPerUser', label: 'Views/User', value: pageviewsPerUser, change: data.pageviewsPerUser?.change || 0, clickable: true, tooltip: 'Average number of pageviews per user, indicating how deeply users explore your site.', metricId: 'pageviewsPerUser' }
      ]
    },
    {
      key: 'engagementRate',
      label: 'Engagement',
      value: `${data.engagementRate.value.toFixed(1)}%`,
      valueLabel: 'Engagement Rate',
      change: data.engagementRate.change,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
        </svg>
      ),
      positive: true,
      clickable: true,
      tooltip: "Percentage of sessions where users were engaged. A session is engaged if it lasts longer than 10 seconds, has 2+ pageviews, or includes a conversion.",
      subStats: [
        { key: 'engagementTime', label: 'Engagement Time', value: formatShortTimeDuration(Math.round(engagementTime)), change: data.engagementTime?.change || null, clickable: true, tooltip: 'Average time users spent actively engaged with your site.', metricId: 'engagementTime' }
      ]
    },
    {
      key: 'topGeos',
      label: 'Top Geos',
      value: '',
      hideChange: true,
      hideMainValue: true,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      ),
      positive: true,
      clickable: false,
      tooltip: "Top 6 countries by users. Click a country to filter by that location.",
      topGeos: topGeos,
      showTopGeos: true,
      selectedGeo: filters?.geo || 'all',
      onGeoClick: onGeoFilter
    },
    {
      key: 'revenue',
      label: 'Revenue',
      value: data.revenue?.value === null || data.revenue?.value === undefined ? null : formatCurrency(data.revenue.value),
      valueLabel: 'Total Revenue',
      change: data.revenue?.change || null,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      ),
      positive: true,
      clickable: true,
      tooltip: "Total AdSense revenue generated from your website. This is the total earnings from ads displayed on your pages.",
      needsAdSense: !selectedSite?.adsenseAccountId && !selectedSite?.adsenseAccountName && !isDemoUser,
      subStats: [
        { 
          key: 'rpu', 
          label: 'RPU', 
          value: data.rpu?.value === null || data.rpu?.value === undefined ? null : formatCurrency(data.rpu.value), 
          change: data.rpu?.change || null, 
          clickable: true,
          tooltip: 'Revenue Per User - Average revenue generated per user.',
          metricId: 'rpu'
    },
    {
      key: 'rpm',
      label: 'RPM',
          value: data.rpm?.value === null || data.rpm?.value === undefined ? null : `$${data.rpm.value.toFixed(2)}`, 
          change: data.rpm?.change || null, 
      clickable: true,
          tooltip: 'Revenue Per Mille - Estimated revenue per 1,000 pageviews.',
          metricId: 'rpm'
        },
        { 
          key: 'cpm', 
          label: 'CPM', 
          value: data.cpm?.value === null || data.cpm?.value === undefined ? null : `$${data.cpm.value.toFixed(2)}`, 
          change: data.cpm?.change || null, 
          clickable: true,
          tooltip: 'Cost Per Mille - Average cost per 1,000 ad impressions.',
          metricId: 'cpm'
        }
      ]
    }
  ];

  const viewingRecentData = isViewingRecentData();

  return (
    <div className="metrics-cards">
      {viewingRecentData && (
        <div style={{
          gridColumn: '1 / -1',
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          background: 'rgba(251, 191, 36, 0.1)',
          border: '1px solid rgba(251, 191, 36, 0.3)',
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
          color: '#92400e',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" />
          </svg>
          <span>
            <strong>Note:</strong> Engagement data for today and yesterday may be incomplete due to Google Analytics processing delays. Engagement metrics typically stabilize after 24-48 hours.
          </span>
        </div>
      )}
      {metrics.map((metric, index) => {
        const isSelected = selectedMetric === metric.key;
        const isClickable = metric.clickable;
        
        return (
          <div 
            key={index} 
            className={`metric-card ${isClickable ? 'clickable' : ''} ${isSelected ? 'selected' : ''}`}
            onClick={isClickable ? () => onMetricSelect(metric.key) : undefined}
            style={{ cursor: isClickable ? 'pointer' : 'default' }}
          >
            <div className="metric-header">
              <div className="metric-icon">{metric.icon}</div>
              <span className="metric-label">{metric.label}</span>
            </div>
            {!metric.hideMainValue && (
            <div className="metric-value">
              {metric.valueLabel && (
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem' }}>
                  {metric.valueLabel}
                  {metric.tooltip && (
                    <Tooltip content={metric.tooltip} metricId={metric.key}>
                      <div className="tooltip-icon" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', opacity: 1, color: '#4b5563' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" fill="none" />
                          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </div>
                    </Tooltip>
                  )}
                </div>
              )}
              {metric.value === null || metric.value === undefined ? 'N/A' : (typeof metric.value === 'number' ? formatNumber(metric.value) : metric.value)}
              </div>
            )}
            {!metric.hideChange && metric.change !== null && metric.change !== undefined && metric.value !== null && (
              <div className={`metric-change ${metric.change > 0 ? 'positive' : metric.change < 0 ? 'negative' : 'neutral'}`}>
                {getChangeIcon(metric.change)}
                <span>{formatChange(metric.change)}</span>
                <span className="vs-previous">vs previous {formatTimeRangeDisplay()} days</span>
              </div>
            )}
            {metric.needsAdSense && (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.75rem',
                background: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                borderRadius: '0.5rem',
                fontSize: '0.75rem',
                color: '#92400e',
                textAlign: 'center'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Connect AdSense</div>
                <div style={{ fontSize: '0.6875rem', opacity: 0.9 }}>
                  Connect your AdSense account to see revenue data
                </div>
              </div>
            )}
            {metric.subStats && (
              <div className="sub-stats">
                {metric.subStats.map((subStat) => (
                  <div 
                    key={subStat.key}
                    className={`sub-stat ${subStat.clickable ? 'clickable' : ''} ${selectedMetric === subStat.key ? 'selected' : ''}`}
                    onClick={subStat.clickable ? (e) => {
                      e.stopPropagation();
                      onMetricSelect(subStat.key);
                    } : undefined}
                    style={{ cursor: subStat.clickable ? 'pointer' : 'default' }}
                  >
                    <div className="sub-stat-label">
                      {subStat.tooltip && (
                        <div className="tooltip-wrapper">
                          <Tooltip content={subStat.tooltip} metricId={subStat.metricId}>
                            <div className="tooltip-icon" style={{ width: '14px', height: '14px', opacity: 1, color: '#4b5563', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'help' }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" fill="none" />
                                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              </svg>
                            </div>
                          </Tooltip>
                        </div>
                      )}
                      <span>{subStat.label}</span>
                    </div>
                    <span className="sub-stat-value">
                      {subStat.value === null || subStat.value === undefined ? 'N/A' : (typeof subStat.value === 'string' ? subStat.value : formatNumber(subStat.value))}
                    </span>
                    {subStat.change !== undefined && subStat.change !== null && subStat.value !== null && (
                      <div className={`sub-stat-change ${subStat.change > 0 ? 'positive' : subStat.change < 0 ? 'negative' : 'neutral'}`}>
                        {getChangeIcon(subStat.change)}
                        <span>{formatChange(subStat.change)}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {metric.showDeviceBreakdown && metric.deviceBreakdown && (
              <div className="device-breakdown">
                <div 
                  className="device-item clickable"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMetricSelect('desktopPercentage');
                  }}
                >
                  <span className="device-icon">💻</span>
                  <span className="device-percentage">{metric.deviceBreakdown.desktop?.percentage || 0}%</span>
                  {metric.deviceBreakdown.desktop?.change !== null && metric.deviceBreakdown.desktop?.change !== undefined && (
                    <div className={`device-change ${metric.deviceBreakdown.desktop.change > 0 ? 'positive' : metric.deviceBreakdown.desktop.change < 0 ? 'negative' : 'neutral'}`}>
                      {getChangeIcon(metric.deviceBreakdown.desktop.change)}
                      <span>{formatChange(metric.deviceBreakdown.desktop.change)}</span>
                    </div>
                  )}
                </div>
                <div 
                  className="device-item clickable"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMetricSelect('mobilePercentage');
                  }}
                >
                  <span className="device-icon">📱</span>
                  <span className="device-percentage">{metric.deviceBreakdown.mobile?.percentage || 0}%</span>
                  {metric.deviceBreakdown.mobile?.change !== null && metric.deviceBreakdown.mobile?.change !== undefined && (
                    <div className={`device-change ${metric.deviceBreakdown.mobile.change > 0 ? 'positive' : metric.deviceBreakdown.mobile.change < 0 ? 'negative' : 'neutral'}`}>
                      {getChangeIcon(metric.deviceBreakdown.mobile.change)}
                      <span>{formatChange(metric.deviceBreakdown.mobile.change)}</span>
                    </div>
                  )}
                </div>
                <div 
                  className="device-item clickable"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMetricSelect('tabletPercentage');
                  }}
                >
                  <span className="device-icon">📟</span>
                  <span className="device-percentage">{metric.deviceBreakdown.tablet?.percentage || 0}%</span>
                  {metric.deviceBreakdown.tablet?.change !== null && metric.deviceBreakdown.tablet?.change !== undefined && (
                    <div className={`device-change ${metric.deviceBreakdown.tablet.change > 0 ? 'positive' : metric.deviceBreakdown.tablet.change < 0 ? 'negative' : 'neutral'}`}>
                      {getChangeIcon(metric.deviceBreakdown.tablet.change)}
                      <span>{formatChange(metric.deviceBreakdown.tablet.change)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            {metric.showTopGeos && (
              Array.isArray(metric.topGeos) && metric.topGeos.length > 0 ? (
                <div className="top-geos-table">
                  <div className="top-geos-header">
                    <span>Country</span>
                    <span>Users</span>
                    <span>Change</span>
                  </div>
                  {metric.topGeos.slice(0, 6).map((geo) => {
                    const country = geo?.country || 'Unknown';
                    const isSelectedGeo = metric.selectedGeo === country;
                    const userCount = Number.isFinite(geo?.users) ? geo.users : 0;
                    const changeValue = Number.isFinite(geo?.changeUsers) ? geo.changeUsers : null;
                    const changeClass =
                      changeValue === null
                        ? 'neutral'
                        : changeValue > 0
                          ? 'positive'
                          : changeValue < 0
                            ? 'negative'
                            : 'neutral';

                    return (
                      <div
                        key={`${country}-${geo?.users ?? 0}`}
                        className={`top-geos-row clickable ${isSelectedGeo ? 'selected' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (metric.onGeoClick) {
                            metric.onGeoClick(isSelectedGeo ? 'all' : country);
                          }
                        }}
                      >
                        <span className="geo-name">{country}</span>
                        <span className="geo-users">{formatNumber(userCount)}</span>
                        <span className={`geo-change ${changeClass}`}>
                          {changeValue === null ? (
                            '—'
                          ) : (
                            <>
                              {getChangeIcon(changeValue)}
                              {formatChange(changeValue)}
                            </>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="top-geos-empty">
                  <span>No geo data yet.</span>
                </div>
              )
            )}
            {isClickable && (
              <div className="chart-indicator">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18M7 12l4-4 4 4 4-4" />
                </svg>
                <span>Click to chart</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MetricsCards;

